import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, CheckCircle, AlertTriangle } from 'lucide-react';

interface QuantitiesMatrixProps {
  specifications: any[];
  deliveryLocations: any[];
  matrix: any;
  onPercentageChange: (specId: string, locationId: string, percentage: number) => void;
  readOnly?: boolean;
}

export function QuantitiesMatrix({ 
  specifications, 
  deliveryLocations,
  matrix,
  onPercentageChange,
  readOnly = false
}: QuantitiesMatrixProps) {
  const getPercentage = (specId: string, locationId: string) => {
    return matrix[specId]?.[locationId]?.percentage || 0;
  };

  const getSpecificationTotal = (specId: string): number => {
    if (!matrix[specId]) return 0;
    return Object.values(matrix[specId] as Record<string, any>).reduce(
      (sum: number, item: any) => sum + (Number(item.percentage) || 0), 
      0
    );
  };

  const getLocationTotal = (locationId: string): number => {
    let total = 0;
    Object.values(matrix).forEach((specMatrix: any) => {
      if (specMatrix[locationId]) {
        total += Number(specMatrix[locationId].percentage) || 0;
      }
    });
    return total;
  };

  const isSpecificationComplete = (specId: string): boolean => {
    const total = getSpecificationTotal(specId);
    return Math.abs(total - 100) < 0.01; // Allow for small floating point errors
  };

  const isSpecificationOverAllocated = (specId: string): boolean => {
    const total = getSpecificationTotal(specId);
    return total > 100.01; // Over 100% (with small tolerance)
  };

  const allSpecificationsComplete = () => {
    return specifications.every(spec => isSpecificationComplete(spec.id));
  };

  const validationStatus = allSpecificationsComplete();

  return (
    <div className="space-y-4">
      {!readOnly && !validationStatus && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Distribute percentages for all specifications.</strong> Each row must total 100%.
          </AlertDescription>
        </Alert>
      )}

      {!readOnly && validationStatus && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            All percentages properly distributed! Each specification totals 100%.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Percentage Distribution by Location</CardTitle>
          <CardDescription>
            Distribute percentages to each delivery location. Each row must total 100%. When a vendor is awarded, quantities will be calculated based on these percentages.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border p-3 bg-muted text-left font-semibold sticky left-0 bg-background z-10 min-w-[200px]">
                    Specification
                  </th>
                  {deliveryLocations.map(location => (
                    <th key={location.id} className="border p-3 bg-muted text-center min-w-[140px]">
                      <div className="font-semibold">{location.delivery_name}</div>
                      <div className="text-xs text-muted-foreground mt-1 font-normal">
                        {location.city}, {location.state_province}
                      </div>
                    </th>
                  ))}
                  <th className="border p-3 bg-muted text-center font-semibold min-w-[120px]">
                    Total %
                  </th>
                </tr>
              </thead>
              <tbody>
                {specifications.map(spec => {
                  const total = getSpecificationTotal(spec.id);
                  const isComplete = isSpecificationComplete(spec.id);
                  const isOverAllocated = isSpecificationOverAllocated(spec.id);
                  
                  return (
                    <tr key={spec.id} className={isOverAllocated ? 'bg-red-50' : isComplete ? 'bg-green-50' : ''}>
                      <td className="border p-3 sticky left-0 bg-background z-10">
                        <div className="font-medium">{spec.specification_name}</div>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {spec.specification_type.replace('_', ' ')}
                        </Badge>
                      </td>
                      {deliveryLocations.map(location => (
                        <td key={location.id} className="border p-2">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={getPercentage(spec.id, location.id) || ''}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                onPercentageChange(spec.id, location.id, value);
                              }}
                              className="w-full text-center"
                              placeholder="0"
                              disabled={readOnly}
                            />
                            <span className="text-xs text-muted-foreground">%</span>
                          </div>
                        </td>
                      ))}
                      <td className={`border p-3 text-center font-semibold ${
                        isOverAllocated ? 'bg-red-200 text-red-900' : 
                        isComplete ? 'bg-green-200 text-green-900' : 
                        'bg-yellow-100 text-yellow-900'
                      }`}>
                        {Number(total).toFixed(2)}%
                        {!isComplete && !isOverAllocated && Number(total) < 100 && (
                          <div className="text-xs mt-1">
                            ({(100 - Number(total)).toFixed(2)}% remaining)
                          </div>
                        )}
                        {isOverAllocated && (
                          <div className="text-xs mt-1">
                            ({(Number(total) - 100).toFixed(2)}% over!)
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div className="text-sm text-muted-foreground space-y-2">
                <p><strong>How to use percentage distribution:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Enter the percentage of each quantity option that should go to each location</li>
                  <li>Each specification row must total exactly 100%</li>
                  <li>Green rows indicate correct distribution (100%)</li>
                  <li>Yellow rows indicate incomplete distribution (&lt; 100%)</li>
                  <li>Red rows indicate over-allocation (&gt; 100%, fix required)</li>
                  <li><strong>Example:</strong> If you have 3 quantity options (1000, 2500, 5000 units) and award the 2500 unit option with 60% to Location A and 40% to Location B, Location A will receive 1500 units and Location B will receive 1000 units.</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
